import { DocumentViewer } from '@aecom-ai-engineering/react-components';
import { Spinner } from '@consigli/facade';
import { useGetFileReadUrlQuery } from '@consigli/hooks';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@consigli/shadcn';
import { LayoutOptimizationResult, File as ConsigliFile } from '@consigli/types';
import { ColumnDef } from '@tanstack/react-table';
import { FC, useMemo } from 'react';

import {
  DataTable,
  ObjectDetailView,
  HTMLViewerEditor,
  createBackofficeRenderers,
} from '@/components';
import { isHTMLDocumentType, resolveViewerFileType } from '@/utils/document-check';

import { DeleteResultModal, EditResultTypeModal } from './modals';
import { ResultsProvider, useResultsContext } from './results-context';
import { useResultsTableConfig } from './table-config';

const ResultFileViewer: FC<{
  projectId: number;
  file: ConsigliFile;
  resultId?: string;
  onClose: () => void;
}> = ({ projectId, file, resultId, onClose }) => {
  const { data, isLoading } = useGetFileReadUrlQuery({ projectId, fileId: file.id });
  const renderers = useMemo(() => createBackofficeRenderers(), []);
  const filename = file.originalFilename;
  const isHTML = isHTMLDocumentType(filename);

  if (!data?.readUrl) {
    if (isLoading) {
      return (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
          role="presentation"
        >
          <Spinner />
        </div>
      );
    }
    return null;
  }

  if (isHTML) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent
          showCloseButton
          className="!max-w-[95vw] max-h-[95vh] w-[95vw] h-[90vh] flex flex-col p-0 gap-0 pt-10"
          aria-describedby={undefined}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>HTML Editor</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 w-full h-full overflow-hidden">
            <HTMLViewerEditor fileUrl={data.readUrl} onClose={onClose} resultId={resultId} />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <DocumentViewer
      open
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      src={data.readUrl}
      title={filename}
      fileName={filename}
      fileType={resolveViewerFileType(filename)}
      showSidebar={false}
      renderers={renderers}
    />
  );
};

const ResultsTabContent = () => {
  const {
    results,
    isLoading,
    selectedItem,
    isDetailViewOpen,
    setIsDetailViewOpen,
    setSelectedItem,
    previewFile,
    previewResultId,
    setPreviewFile,
    projectId,
  } = useResultsContext();

  const columns = useResultsTableConfig();

  return (
    <>
      <div className="w-full">
        <DataTable<LayoutOptimizationResult, unknown>
          columns={columns as ColumnDef<LayoutOptimizationResult, unknown>[]}
          data={results}
          isLoading={isLoading}
          scrollable
          height="60vh"
        />
      </div>

      <DeleteResultModal />
      <EditResultTypeModal />

      {selectedItem && (
        <ObjectDetailView
          object={selectedItem}
          objectType="Result"
          summary={`Result "${selectedItem.file.originalFilename}" details including type and metadata.`}
          isOpen={isDetailViewOpen}
          onOpenChange={(open: boolean) => {
            setIsDetailViewOpen(open);
            if (!open) {
              setSelectedItem(null);
            }
          }}
        />
      )}

      {previewFile && (
        <ResultFileViewer
          projectId={projectId}
          file={previewFile}
          resultId={previewResultId ?? undefined}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </>
  );
};

export const ResultsTab = () => {
  return (
    <ResultsProvider>
      <ResultsTabContent />
    </ResultsProvider>
  );
};
